// TelemetryDashboard · 6-tile live NOC-style monitor that replaces
// the 4 marketing pillar cards. Each tile pairs a headline metric
// with a live mini-visualisation (sparkline / bar / area / gauge /
// donut / ring) so the section feels like real infrastructure, not
// copywriting. All data is fake but updates on a heartbeat so the
// dashboard looks alive.
import { useEffect, useState, useMemo } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Activity, Zap, TrendingDown, Server, Network, ShieldCheck } from "lucide-react";
import LiveTerminal from "@/components/LiveTerminal";

/* Random-walk helper · used to seed initial sparkline data */
function walk(n, start, drift = 0, noise = 1) {
  const out = [start];
  for (let i = 1; i < n; i++) {
    const v = out[i - 1] + drift + (Math.random() - 0.5) * noise;
    out.push(v);
  }
  return out;
}

/* ===== Mini sparkline · area + line ===== */
let __sparkIdCounter = 0;
function Sparkline({ data, color, w = 120, h = 36 }) {
  // Use a guaranteed-safe gradient id · the previous `color.slice(1)`
  // trick produced invalid ids whenever the colour was a CSS variable
  // like `var(--brand)` (slice → `ar(--brand)`), which broke the area
  // fill and made the sparkline render as solid black. Hook called
  // BEFORE the early-return below to comply with rules-of-hooks.
  const gradId = useMemo(() => `spark-${++__sparkIdCounter}`, []);
  // Guard · skip rendering if we don't have enough points. Otherwise
  // `last[0]` / `last[1]` would emit React's "cx/cy: undefined" SVG
  // warnings during the first paint before live data arrives.
  if (!Array.isArray(data) || data.length < 2) return null;
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  const pts = data.map((v, i) => {
    const x = (i / (data.length - 1)) * w;
    const y = h - ((v - min) / range) * (h - 3) - 1.5;
    return [x, y];
  });
  const line = pts.map((p) => p.join(",")).join(" ");
  const area = `${line} ${w},${h} 0,${h}`;
  const last = pts[pts.length - 1];
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-full" preserveAspectRatio="none" aria-hidden>
      <defs>
        <linearGradient id={gradId} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.35" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <polygon points={area} fill={`url(#${gradId})`} />
      <polyline points={line} fill="none" stroke={color} strokeWidth="1.2" strokeLinejoin="round" strokeLinecap="round" />
      <circle cx={last[0]} cy={last[1]} r="1.8" fill={color} />
    </svg>
  );
}

/* ===== Mini bar chart ===== */
function BarChart({ data, color, w = 120, h = 36 }) {
  if (!Array.isArray(data) || data.length === 0) return null;
  const max = Math.max(...data) || 1;
  const slot = w / data.length;
  const bw = Math.max(1.6, slot * 0.62);
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-full" preserveAspectRatio="none" aria-hidden>
      {data.map((v, i) => {
        const bh = Math.max(1.2, (v / max) * (h - 3));
        const x = slot * i + (slot - bw) / 2;
        return (
          <rect
            key={i}
            x={x}
            y={h - bh - 1}
            width={bw}
            height={bh}
            rx="0.6"
            fill={color}
            opacity={i === data.length - 1 ? 1 : 0.45 + (i / data.length) * 0.5}
          />
        );
      })}
    </svg>
  );
}

/* ===== Circular ring gauge ===== */
function RingGauge({ value, target = 100, color, size = 56, label }) {
  const stroke = 4.5;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const pct = Math.min(1, value / target);
  const dash = c * pct;
  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      <svg viewBox={`0 0 ${size} ${size}`} className="absolute inset-0 -rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none"
                stroke="color-mix(in srgb, var(--text-3) 18%, transparent)" strokeWidth={stroke} />
        <motion.circle
          cx={size / 2} cy={size / 2} r={r}
          fill="none" stroke={color} strokeWidth={stroke} strokeLinecap="round"
          strokeDasharray={c}
          initial={{ strokeDashoffset: c }}
          whileInView={{ strokeDashoffset: c - dash }}
          viewport={{ once: true }}
          transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
        />
      </svg>
      <div className="absolute inset-0 grid place-items-center">
        <span className="font-mono text-[9.5px] uppercase tracking-[0.14em] text-text-3">{label}</span>
      </div>
    </div>
  );
}

/* ===== Donut · 4 segments ===== */
function Donut({ slices, size = 56 }) {
  const stroke = 7;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const total = slices.reduce((s, x) => s + x.v, 0);
  // Pre-compute cumulative offsets so the render loop doesn't mutate
  // an outer accumulator (immutability lint rule).
  const offsets = slices.reduce((acc, s) => {
    const last = acc[acc.length - 1] ?? 0;
    acc.push(last + s.v / total);
    return acc;
  }, []);
  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      <svg viewBox={`0 0 ${size} ${size}`} className="-rotate-90">
        {slices.map((s, i) => {
          const f = s.v / total;
          const dash = c * f;
          const off = c * (1 - (offsets[i - 1] ?? 0));
          return (
            <circle
              key={i}
              cx={size / 2} cy={size / 2} r={r}
              fill="none" stroke={s.c} strokeWidth={stroke}
              strokeDasharray={`${dash} ${c - dash}`}
              strokeDashoffset={off}
            />
          );
        })}
      </svg>
      <div className="absolute inset-0 grid place-items-center">
        <span className="font-mono text-[12px] uppercase tracking-[0.14em] text-text-3">{total}</span>
      </div>
    </div>
  );
}

/* ===== Generic tile wrapper · fixed height locked from parent grid
 * via auto-rows so the dashboard never bobs as live data updates. */
function Tile({ label, Icon, value, unit, trend, color, children, testId }) {
  const up = (trend ?? 0) >= 0;
  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-10% 0px" }}
      transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
      className="relative rounded-xl p-3 lg:p-3.5 overflow-hidden group h-full flex flex-col"
      style={{
        borderColor: "var(--line)",
        borderWidth: "1px",
        borderStyle: "solid",
        background: "color-mix(in srgb, var(--surface) 60%, transparent)",
      }}
      data-testid={testId}
    >
      {/* Top-right corner glow on hover */}
      <div
        aria-hidden
        className="absolute -top-12 -right-12 w-28 h-28 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
        style={{ background: `radial-gradient(circle, color-mix(in srgb, ${color} 22%, transparent), transparent 65%)` }}
      />
      {/* Header · fixed height to lock vertical layout */}
      <div className="flex items-center justify-between mb-2 h-[14px] gap-1.5">
        <div className="inline-flex items-center gap-1.5 font-mono text-[9.5px] sm:text-[12px] uppercase tracking-[0.12em] sm:tracking-[0.22em] text-text-3 min-w-0 truncate">
          <Icon size={10} style={{ color }} className="shrink-0" />
          <span className="truncate">{label}</span>
        </div>
        {trend != null && (
          <span
            className="font-mono text-[10px] sm:text-[12px] tabular-nums px-1.5 py-0.5 rounded-full leading-none shrink-0"
            style={{
              background: up ? "rgba(80,220,130,0.12)" : "rgba(240,106,106,0.12)",
              color: up ? "#54d97c" : "#f06a6a",
            }}
          >
            {up ? "▲" : "▼"} {Math.abs(trend).toFixed(1)}%
          </span>
        )}
      </div>
      {/* Value row · fixed height locks the baseline so font hinting
          variations between numbers don't push the visualisation up or
          down on each heartbeat tick. */}
      <div className="flex items-end gap-1.5 mb-2 h-[24px] min-w-0">
        <span
          className="font-display text-[18px] sm:text-[22px] font-semibold leading-none tabular-nums text-text-1 inline-block shrink-0"
        >
          {value}
        </span>
        {unit && (
          <span className="font-mono text-[9.5px] uppercase tracking-[0.14em] text-text-3 pb-0.5 leading-none truncate min-w-0">{unit}</span>
        )}
      </div>
      {/* Visualisation · fixed height container */}
      <div className="h-[36px]">{children}</div>
    </motion.div>
  );
}

export default function TelemetryDashboard() {
  const { t } = useTranslation();
  // Live state · 6 metrics with sparkline history each.
  const [tick, setTick] = useState(0);
  const [history, setHistory] = useState(() => ({
    latency: walk(24, 12.4, -0.02, 0.7),
    orders:  walk(24, 820, 1.2, 28),
    spread:  walk(24, 0.42, -0.001, 0.04),
    network: walk(24, 2.1, 0.005, 0.12),
  }));

  useEffect(() => {
    // Mean-reverting random walk · each metric drifts gently towards
    // its anchor value, so digit-count stays stable (e.g. orders
    // never crosses 1000 → no width shift on the tile). Clamps cap
    // the range hard so a long session can't accumulate drift.
    const t = setInterval(() => {
      setTick((x) => x + 1);
      const reverting = (cur, anchor, vol, min, max) => {
        const noise = (Math.random() - 0.5) * vol;
        const pull = (anchor - cur) * 0.08;
        return Math.min(max, Math.max(min, cur + noise + pull));
      };
      setHistory((prev) => ({
        latency: [...prev.latency.slice(1), reverting(prev.latency.at(-1), 12.5, 1.2, 10, 16)],
        orders:  [...prev.orders.slice(1),  reverting(prev.orders.at(-1),  860,  28,  780, 960)],
        spread:  [...prev.spread.slice(1),  reverting(prev.spread.at(-1),  0.48, 0.06, 0.32, 0.66)],
        network: [...prev.network.slice(1), reverting(prev.network.at(-1), 2.30, 0.14, 1.85, 2.78)],
      }));
    }, 1400);
    return () => clearInterval(t);
  }, []);

  const last = useMemo(() => ({
    latency: Math.round(history.latency.at(-1)),
    orders:  Math.round(history.orders.at(-1)),
    spread:  history.spread.at(-1).toFixed(2),
    network: history.network.at(-1).toFixed(2),
  }), [history, tick]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div
      className="relative rounded-2xl p-5 sm:p-6 lg:p-7"
      style={{
        borderColor: "color-mix(in srgb, var(--brand) 10%, var(--line))",
        borderWidth: "1px",
        borderStyle: "solid",
        background: "linear-gradient(180deg, color-mix(in srgb, var(--surface) 55%, transparent), color-mix(in srgb, var(--surface-2) 55%, transparent))",
        backdropFilter: "blur(6px)",
      }}
      data-testid="liquidity-telemetry"
    >
      {/* Header strip · NOC-style */}
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div className="flex items-center gap-2.5 min-w-0">
          <span className="inline-flex items-center justify-center w-7 h-7 rounded-lg"
                style={{ background: "color-mix(in srgb, var(--brand) 14%, transparent)", border: "1px solid color-mix(in srgb, var(--brand) 30%, transparent)" }}>
            <Activity size={13} style={{ color: "var(--brand)" }} />
          </span>
          <div className="leading-tight">
            <div className="font-mono text-[9.5px] uppercase tracking-[0.22em] text-text-3">{t("telemetry.eyebrow", "Telemetry · LD4-A1")}</div>
            <div className="font-display text-[14px] font-medium text-text-1">{t("telemetry.title", "Trading-floor live metrics")}</div>
          </div>
        </div>
        <div className="inline-flex items-center gap-1.5 font-mono text-[12px] uppercase tracking-[0.2em] text-text-3">
          <motion.span
            className="inline-block w-1.5 h-1.5 rounded-full"
            style={{ background: "#54d97c", boxShadow: "0 0 6px #54d97c" }}
            animate={{ opacity: [1, 0.25, 1] }}
            transition={{ duration: 1.2, repeat: Infinity }}
          />
          <span style={{ color: "#54d97c" }}>{t("telemetry.streaming", "Streaming")}</span>
          <span className="text-text-3">· {t("telemetry.refresh", "refresh")} 1.4s</span>
        </div>
      </div>

      {/* Tile grid · responsive layout · FIXED row heights so the
          section never grows or shrinks as live data ticks.
          • Mobile (2-col, h-32 rows): 2 tiles · terminal full-width
            (h-72) · 4 tiles (2×2).
          • Desktop (4-col): 3 tiles · terminal anchored to col-4
            spanning rows 1-2 · 3 tiles. */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-5 auto-rows-[128px] lg:auto-rows-[140px]">
        <Tile
          label={t("telemetry.tiles.latency", "Latency")} Icon={Zap}
          value={last.latency} unit="ms"
          trend={-2.1} color="var(--brand)"
          testId="telemetry-latency"
        >
          <Sparkline data={history.latency} color="var(--brand)" />
        </Tile>

        <Tile
          label={t("telemetry.tiles.orders", "Orders")} Icon={Activity}
          value={last.orders.toLocaleString()} unit="orders/s"
          trend={+3.4} color="#3d8a99"
          testId="telemetry-orders"
        >
          <BarChart data={history.orders.slice(-14)} color="#3d8a99" />
        </Tile>

        {/* LiveTerminal · mobile: full-width row 2 (col-span-2, row-span-2).
            Desktop: anchored to col-4 spanning rows 1-2. Fixed row
            heights from the parent grid (128 px × 2 + 16 px gap = 272 px
            mobile, 140 px × 2 + 20 px gap = 300 px desktop) prevent
            the terminal from growing as new log lines stream. */}
        <div className="col-span-2 row-span-2 lg:col-span-1 lg:row-span-2 lg:col-start-4 lg:row-start-1">
          <LiveTerminal />
        </div>

        <Tile
          label={t("telemetry.tiles.spread", "Spread")} Icon={TrendingDown}
          value={last.spread} unit="bps"
          trend={-0.8} color="#5d6b85"
          testId="telemetry-spread"
        >
          <Sparkline data={history.spread} color="#5d6b85" />
        </Tile>

        <Tile
          label={t("telemetry.tiles.lp", "LP Pool")} Icon={Network}
          value="13 / 13" unit="banks"
          trend={null} color="var(--brand)"
          testId="telemetry-lps"
        >
          <div className="flex items-center gap-1.5 sm:gap-2.5 h-full min-w-0">
            <Donut
              size={26}
              slices={[
                { v: 5, c: "var(--brand)" },
                { v: 3, c: "#3d8a99" },
                { v: 3, c: "#5d6b85" },
                { v: 2, c: "var(--brand-deep)" },
              ]}
            />
            <div className="flex-1 min-w-0 text-[9px] sm:text-[9.5px] font-mono uppercase tracking-[0.06em] sm:tracking-[0.08em] text-text-3 leading-[1.4]">
              <div className="truncate">JPM · GS · MS</div>
              <div className="truncate">DB · UBS · BARC</div>
              <div className="truncate text-text-2">+7 more</div>
            </div>
          </div>
        </Tile>

        <Tile
          label={t("telemetry.tiles.network", "Network")} Icon={Server}
          value={last.network} unit="Gbps"
          trend={+1.2} color="var(--brand)"
          testId="telemetry-network"
        >
          <Sparkline data={history.network} color="var(--brand)" />
        </Tile>

        <Tile
          label={t("telemetry.tiles.uptime", "Uptime")} Icon={ShieldCheck}
          value="99.97" unit="%"
          trend={null} color="var(--brand)"
          testId="telemetry-uptime"
        >
          <div className="flex items-center gap-1.5 sm:gap-2.5 h-full min-w-0">
            <RingGauge value={99.97} target={100} color="var(--brand)" size={26} label="4 9s" />
            <div className="flex-1 min-w-0 text-[9px] sm:text-[9.5px] font-mono uppercase tracking-[0.06em] sm:tracking-[0.08em] text-text-3 leading-[1.4]">
              <div className="truncate">Tgt <span className="text-text-1">99.95%</span></div>
              <div className="truncate">Q1 inc <span className="text-text-1">0</span></div>
              <div className="truncate text-text-2">SLA 0</div>
            </div>
          </div>
        </Tile>
      </div>
    </div>
  );
}
